import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MessageOutlined from "@mui/icons-material/MessageOutlined";
import Upload from "@mui/icons-material/Upload";
import { Box, Button, Collapse, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useResolvedActualDay, useWeekViewContext } from "../../contexts/WeekView";
import { useNurseOvertimeMap } from "../../hooks/useNurseOvertimeMap";
import { FileUploadModal } from "../../pages/DaySchedule/FileUploadModal";
import { dailyViewStaffTableSortingKey, localStorageService } from "../../services/LocalStorageService";
import { INK, STATUS, STATUS_BADGE, SURFACE } from "../../theme/tokens";
import { hasHrisIntegrationOfType } from "../../utils/hrisIntegrations";
import type { BaseNurseShift, DayItem, InvalidActualNurses, Nurse } from "../../utils/models";
import type { SortingState } from "../../utils/multiColumnSorting";
import { MessageWorkingFilterEnum } from "../StaffCommunication/util/Broadcast/MessageFilter";
import { NewBroadcastMessageModal } from "../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { StaffTable } from "../StaffTable";
import { defaultSortStateStaffTable, type NurseInShiftWithWage, staffTableColumns } from "../StaffTable/StaffTable";
import { type SortingButtonHandle, SystemSortingButtonWithPopup } from "../UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { DAY_VIEW_CONTENT_PADDING } from "./consts";
import { DayViewFilters } from "./DayViewFilters";
import { DayViewTimeline } from "./DayViewTimeline";
import { useShiftFilters } from "./useShiftFilters";

type DayViewContentActualProps = {
	date: DateTime;
	weekIndex: number;
	nurses: BaseNurseShift[];
	selectedNurse: Nurse | null;
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
};
export const DayViewContentActual: FC<DayViewContentActualProps> = ({
	date,
	weekIndex,
	nurses,
	selectedNurse,
	selectedLocations,
	setSelectedLocations,
	availableLocations,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { weekViewData } = useWeekViewContext();
	const { chosenDepartment, getWeekRange, timezone } = useHospitalData();
	const hasShiftsIntegration = hasHrisIntegrationOfType(chosenDepartment, "shifts");
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const dayItem = useMemo<DayItem>(() => weekViewData[date.weekday % 7], [weekViewData, date]);

	const { actualShifts: actualNurses, missingFromScheduled } = useResolvedActualDay(date);

	const { startDate: otStartDate, endDate: otEndDate } = getWeekRange(date);
	const { data: otMap } = useNurseOvertimeMap(chosenDepartment.department_id, otStartDate, otEndDate, timezone, true);
	const otDateKey = date.toISODate() ?? undefined;

	const invalidActualNurses = useMemo<InvalidActualNurses>(() => {
		const missingPunchIn: BaseNurseShift[] = [];
		const missingPunchOut: BaseNurseShift[] = [];
		const missingBothPunches: BaseNurseShift[] = [];

		actualNurses
			.filter((nurseShift) => {
				return !dayItem.shifts.some((scheduledShift) => {
					return (
						nurseShift.nurseId === scheduledShift.nurseId && nurseShift.startTime <= scheduledShift.endTime && nurseShift.endTime >= scheduledShift.startTime
					);
				});
			})
			.forEach((nurseShift) => {
				if (nurseShift.missingPunchIn && nurseShift.missingPunchOut) {
					missingBothPunches.push(nurseShift);
				} else if (nurseShift.missingPunchIn) {
					missingPunchIn.push(nurseShift);
				} else if (nurseShift.missingPunchOut) {
					missingPunchOut.push(nurseShift);
				}
			});

		return { missingFromScheduled, missingPunchIn, missingPunchOut, missingBothPunches };
	}, [dayItem, actualNurses, missingFromScheduled]);

	const invalidActualNursesList = useMemo<BaseNurseShift[]>(
		() => [
			...invalidActualNurses.missingPunchIn,
			...invalidActualNurses.missingPunchOut,
			...invalidActualNurses.missingBothPunches,
			...invalidActualNurses.missingFromScheduled,
		],
		[invalidActualNurses],
	);

	const {
		selectedRoles,
		selectedJobs,
		selectedTimes,
		selectedOtBadgeTypes,
		isExpanded,
		setIsExpanded,
		sliderStart,
		setSliderStart,
		sliderEnd,
		setSliderEnd,
		filterStaff,
		staff,
		shiftTimeFilters,
		onRoleTypeFilterChange,
		onJobTypeFilterChange,
		onShiftTimeFilterChange,
		onOtBadgeFilterChange,
		onShiftClick,
	} = useShiftFilters({
		dayItem,
		date,
		nurses: actualNurses,
		selectedNurse,
		selectedLocations,
		availableLocations,
		showActual: true,
		shiftsForTimeFilters: dayItem.shifts,
		otMap,
		otDateKey,
	});

	const [showBroadcastModal, setShowBroadcastModal] = useState<boolean>(false);
	const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
	const sortingButtonRef = useRef<SortingButtonHandle>(null);
	const storedSortingState = useMemo(() => localStorageService.getTableSortingState<NurseInShiftWithWage>(dailyViewStaffTableSortingKey, userId), [userId]);
	const [sortState, setSortState] = useState<SortingState<NurseInShiftWithWage>[]>(() => {
		return storedSortingState && storedSortingState.length > 0 ? storedSortingState : defaultSortStateStaffTable;
	});
	const [showHRISMissingDataBanner, setShowHRISMissingDataBanner] = useState<boolean>(() => !nurses.length);

	const filteredInvalidActualNursesList = useMemo<BaseNurseShift[]>(() => filterStaff(invalidActualNursesList), [invalidActualNursesList, filterStaff]);

	const invalidNurseIds = useMemo<number[]>(() => {
		const missingPunchInIds = invalidActualNurses.missingPunchIn.map((nurse) => nurse.nurseId);
		const missingPunchOutIds = invalidActualNurses.missingPunchOut.map((nurse) => nurse.nurseId);
		const missingBothPunchesIds = invalidActualNurses.missingBothPunches.map((nurse) => nurse.nurseId);
		const missingFromScheduledIds = invalidActualNurses.missingFromScheduled.map((nurse) => nurse.nurseId);
		return Array.from(new Set([...missingPunchInIds, ...missingPunchOutIds, ...missingBothPunchesIds, ...missingFromScheduledIds]));
	}, [invalidActualNurses]);

	const nurseIdsToIgnore = useMemo<number[]>(() => {
		const allNurseIds = [...actualNurses, ...invalidActualNursesList].map((nurse) => nurse.nurseId);
		const uniqueNurseIds = Array.from(new Set(allNurseIds));
		return uniqueNurseIds.filter((nurseId) => !invalidNurseIds.includes(nurseId));
	}, [actualNurses, invalidActualNursesList, invalidNurseIds]);

	const showWarning = useMemo<boolean>(() => {
		return (
			showHRISMissingDataBanner &&
			hasShiftsIntegration &&
			(!!invalidActualNurses.missingPunchIn.length || !!invalidActualNurses.missingPunchOut.length || !!invalidActualNurses.missingBothPunches.length)
		);
	}, [invalidActualNurses, hasShiftsIntegration, showHRISMissingDataBanner]);

	useEffect(() => {
		setShowHRISMissingDataBanner(!nurses.length);
	}, [nurses]);

	return (
		<Box sx={{ width: "100%", minHeight: "100%", display: "flex", flexDirection: "column", p: DAY_VIEW_CONTENT_PADDING, gap: 1 }}>
			<Box sx={{ flexShrink: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
					<SystemSortingButtonWithPopup
						ref={sortingButtonRef}
						sortingOptions={staffTableColumns}
						localStorageTableKey={dailyViewStaffTableSortingKey}
						setSortState={setSortState}
						initialState={sortState}
						defaultSortState={defaultSortStateStaffTable}
					/>
					<DayViewFilters
						onRoleTypeFilterChange={onRoleTypeFilterChange}
						onJobTypeFilterChange={onJobTypeFilterChange}
						onShiftTimeFilterChange={onShiftTimeFilterChange}
						shiftsTimeFilters={shiftTimeFilters}
						selectedShiftTimes={selectedTimes}
						selectedRoleTypes={selectedRoles}
						selectedJobs={selectedJobs}
						selectedLocations={selectedLocations}
						setSelectedLocations={setSelectedLocations}
						availableLocations={availableLocations}
						selectedOtBadgeTypes={selectedOtBadgeTypes}
						onOtBadgeFilterChange={onOtBadgeFilterChange}
					/>
					<Box
						component={Button}
						sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
						onClick={() => setIsExpanded((expanded) => !expanded)}
					>
						<Typography sx={{ fontWeight: 600, fontSize: { xs: 12, xl: 14 }, color: INK.SECONDARY }}>
							{isExpanded ? "Hide Timeline" : "Show Timeline"}
						</Typography>
						<ArrowDownward
							sx={{
								color: INK.MUTED,
								transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
								transition: "transform 0.3s ease",
								fontSize: { xs: 16, xl: 20 },
							}}
						/>
					</Box>
				</Box>
				<Collapse in={isExpanded}>
					<DayViewTimeline
						date={date}
						nurses={staff}
						requests={[]}
						shiftStart={dayItem.earliestTime.set({ minute: 0 })}
						shiftEnd={dayItem.latestTime.set({ minute: 0 })}
						startTime={sliderStart.set({ minute: 0 })}
						setStartTime={setSliderStart}
						endTime={sliderEnd.set({ minute: 0 })}
						setEndTime={setSliderEnd}
						onShiftClick={onShiftClick}
						selectedRoles={selectedRoles === "All" ? [] : selectedRoles}
						selectedNurse={selectedNurse}
					/>
				</Collapse>
			</Box>
			{showWarning && (
				<Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
					<Box
						sx={{
							alignItems: "center",
							gap: 1,
							px: 2,
							py: 1.5,
							backgroundColor: STATUS.WARNING.light,
							width: "700px",
							border: `1px solid ${STATUS_BADGE.PEACH}`,
							borderRadius: "8px",
						}}
					>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
								<InfoOutlined sx={{ fontSize: "20px", color: STATUS_BADGE.ORANGE }} />
								<Typography sx={(theme) => ({ fontSize: "16px", fontWeight: 600, color: theme.palette.primary.main })}>{t("missing.hris.data")}</Typography>
							</Box>
							<Box sx={{ cursor: "pointer" }} onClick={() => setShowHRISMissingDataBanner(false)}>
								<Close sx={{ fontSize: "20px", color: INK.MUTED }} />
							</Box>
						</Box>
						<Box sx={{ mb: 1.5 }}>
							<Typography sx={{ fontSize: "16px", color: INK.MUTED }}>{t("missing.hris.data.description")}</Typography>
						</Box>
						<Box sx={{ display: "flex", gap: 2 }}>
							{hasShiftsIntegration && (
								<SystemButton
									sx={{ height: "24px", backgroundColor: STATUS_BADGE.ORANGE, color: SURFACE, "&:hover": { backgroundColor: STATUS.WARNING.main } }}
									onClick={() => setShowUploadModal(true)}
									leadingIcon={<Upload />}
								>
									{t("upload.now")}
								</SystemButton>
							)}
						</Box>
					</Box>
				</Box>
			)}
			<Box sx={{ overflow: "auto" }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", bgcolor: SURFACE, p: 1, position: "sticky", top: 0, zIndex: 10 }}>
					<Box sx={{ display: "flex", gap: 2 }}>
						<Typography sx={(theme) => ({ fontWeight: 700, fontSize: "16px", color: theme.palette.primary.main })}>Actual</Typography>
						<Typography sx={(theme) => ({ fontWeight: 700, fontSize: "14px", color: theme.palette.primary.main })}>verified ({staff.length})</Typography>
						<Typography sx={{ fontWeight: 700, fontSize: "14px", color: alpha(STATUS.ERROR.main, 0.8) }}>
							missing ({filteredInvalidActualNursesList.length})
						</Typography>
					</Box>
					<Box>
						<SystemButton
							sx={{ height: "30px" }}
							onClick={() => {
								track("notify_missing_punches_clicked", { date: date.toFormat("yyyy-MM-dd"), count: invalidNurseIds.length });
								setShowBroadcastModal(true);
							}}
							leadingIcon={<MessageOutlined />}
							disabled={!invalidNurseIds.length}
						>
							{t("notify.missing.punches")}
						</SystemButton>
					</Box>
				</Box>

				<StaffTable
					date={date}
					showActual={true}
					staff={staff}
					weekIndex={weekIndex}
					dayIndex={date.weekday % 7}
					requests={[]}
					sortState={sortState}
					setSortState={setSortState}
					onSortHandledFromInside={() => sortingButtonRef.current?.openMenu()}
					invalidNurses={filteredInvalidActualNursesList}
					selectedNurse={selectedNurse}
				/>
			</Box>
			{showBroadcastModal && (
				<NewBroadcastMessageModal
					onClose={() => setShowBroadcastModal(false)}
					nurseIdsToIgnore={nurseIdsToIgnore}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_WORKS}
					initialFilters={[
						{
							workFilter: MessageWorkingFilterEnum.STAFF_THAT_WORKS,
							dateFilter: date,
							timeFilter: { start: date.startOf("day"), end: date.endOf("day") },
						},
					]}
					initialSmsMessage={`Hi, You are missing punches for the ${date.toFormat("MMMM Do")} shift at ${chosenDepartment.display_name}. Please reach out to HR or admin team to fix your timesheet.`}
				/>
			)}
			<Modal open={showUploadModal} onClose={() => setShowUploadModal(false)} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<div>
					<FileUploadModal onClose={() => setShowUploadModal(false)} startDate={date} endDate={date} />
				</div>
			</Modal>
		</Box>
	);
};
